import { z } from 'zod';

/**
 * Zod schema for validating automation plan import JSON.
 *
 * Validation scope:
 * 1. Root: object with "instructions" and/or "plan"; at least one must be present and valid.
 * 2. instructions: if present, array of { id: number|string, text: string }.
 * 3. plan: if present, object with "steps" (array). Step contents are not validated (passthrough).
 */

const instructionItemSchema = z.object({
    id: z.union([z.number(), z.string()]),
    text: z.string(),
});

const instructionsSchema = z.array(instructionItemSchema).optional();

const planSchema = z
    .object({
        steps: z.array(z.any()),
    })
    .passthrough()
    .optional();

const automationPlanImportSchema = z
    .object({
        instructions: instructionsSchema,
        plan: planSchema,
    })
    .passthrough()
    .refine(
        (data) => {
            const hasInstructions =
                data.instructions !== undefined && Array.isArray(data.instructions);
            const hasPlan =
                data.plan !== undefined &&
                data.plan !== null &&
                typeof data.plan === 'object' &&
                !Array.isArray(data.plan) &&
                Array.isArray(data.plan.steps);
            return hasInstructions || hasPlan;
        },
        {
            message:
                'File must contain at least one of "instructions" (array) or "plan" (object with "steps" array)',
        }
    );

/**
 * Validate parsed JSON for automation plan import.
 * @param {unknown} parsed - Result of JSON.parse (or any value).
 * @returns {{ success: true, data: z.infer<typeof automationPlanImportSchema> } | { success: false, error: string }}
 */
export function validateAutomationPlanImport(parsed) {
    const result = automationPlanImportSchema.safeParse(parsed);
    if (result.success) {
        return { success: true, data: result.data };
    }
    const issues = result.error.issues;
    const first = issues[0];
    const path = first.path.length ? first.path.join('.') + ': ' : '';
    const message = path + (first.message || 'Invalid format');
    return { success: false, error: message };
}

export { automationPlanImportSchema };
